import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ORIGINS = {
	right: '100% 0%',
	left: '0% 100%',
};

/**
 * @param {HTMLElement} section
 */
const bindSection = (section) => {
	const motions = section.querySelectorAll('.js-trust-decoration');
	if (!motions.length) {
		return null;
	}

	const paths = section.querySelectorAll('.js-trust-decoration-path');
	const anchors = section.querySelectorAll('.trust__decoration');

	const cleanups = [];

	motions.forEach((motion) => {
		const side = motion.dataset.trustDecoration === 'left' ? 'left' : 'right';
		const origin = ORIGINS[side];
		const anchor = motion.closest('.trust__decoration');

		gsap.set(motion, { transformOrigin: origin });
	});

	paths.forEach((path) => {
		gsap.set(path, { transformOrigin: '50% 50%', opacity: 0.1 });
	});

	let idleTweens = [];

	const entrance = gsap.timeline({
		scrollTrigger: {
			trigger: section,
			start: 'top 80%',
			once: true,
		},
		defaults: { ease: 'power3.out' },
		onComplete: () => {
			motions.forEach((motion) => {
				const idleTween = gsap.to(motion, {
					y: -7,
					duration: 2.6,
					ease: 'sine.inOut',
					yoyo: true,
					repeat: -1,
				});
				idleTweens.push(idleTween);
			});

			paths.forEach((path) => {
				const glowTween = gsap.to(path, {
					opacity: 0.17,
					duration: 2.6,
					ease: 'sine.inOut',
					yoyo: true,
					repeat: -1,
				});
				idleTweens.push(glowTween);
			});
		},
	});

	entrance.fromTo(
		motions,
		{ y: 36, rotation: -12, autoAlpha: 0 },
		{ y: 0, rotation: 0, autoAlpha: 1, duration: 1.15, stagger: 0.12 },
	);

	entrance.fromTo(
		paths,
		{ scale: 0.55, opacity: 0 },
		{ scale: 1, opacity: 0.1, duration: 0.9, ease: 'back.out(1.6)', stagger: 0.1 },
		'-=0.75',
	);

	const parallax = gsap.fromTo(
		anchors,
		{ y: 12 },
		{
			y: -22,
			ease: 'none',
			scrollTrigger: {
				trigger: section,
				start: 'top bottom',
				end: 'bottom top',
				scrub: 0.85,
			},
		},
	);

	cleanups.push(() => {
		entrance.scrollTrigger?.kill();
		parallax.scrollTrigger?.kill();
		idleTweens.forEach((tween) => tween.kill());
		gsap.killTweensOf([...motions, ...paths, ...anchors]);
		gsap.set([...motions, ...paths, ...anchors], { clearProps: 'all' });
		paths.forEach((path) => {
			path.style.opacity = '0.1';
		});
	});

	return () => cleanups.forEach((fn) => fn());
};

export const initTrustDecoration = () => {
	const sections = document.querySelectorAll('.section--trust');
	if (!sections.length) {
		return;
	}

	const cleanups = [];

	gsap.matchMedia().add('(prefers-reduced-motion: no-preference) and (min-width: 1100px)', () => {
		sections.forEach((section) => {
			const cleanup = bindSection(section);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});
};
